import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, Image, StyleSheet, Text, View } from 'react-native';
import { useDispatch } from 'react-redux';

import { ProfileChipPng } from '../../../assets';
import { unlockRoomPurchase } from '../../../services/api';
import { profileRequest } from '../../../store/auth/authActions';
import { toast } from '../../../utils/helpers/toastUtils';
import { useDevice } from '../../../utils/hooks/useDevice';
import { RootStackParamList } from '../../navigator';
import { TouchableOpacity } from '../common/Touchable';
import NotEnoughChipsModal from './NotEnoughChipsModal';
import { redesignPanelColors } from './redesignPanelChrome';
import SvaraModal from './SvaraModal';

interface UnlockRoomModalProps {
	onClose: () => void;
	onUnlocked?: () => void;
	roomId: string;
	roomName: string;
	roomUnlockCost: number;
	minLevel: number;
	lockImages: {
		lock4_1: any;
		lock4_2: any;
	};
}

const UnlockRoomModal = ({
	onClose,
	onUnlocked,
	roomId,
	roomName,
	roomUnlockCost,
	minLevel,
	lockImages,
}: UnlockRoomModalProps) => {
	const { t } = useTranslation();
	const { isDesktop, isLandscape, safeAreaWidth, safeAreaHeight } = useDevice();
	const dispatch = useDispatch();
	const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
	const [isSubmitting, setIsSubmitting] = React.useState(false);
	const [showNotEnoughChips, setShowNotEnoughChips] = React.useState(false);
	const isDesktopLandscape = isDesktop && isLandscape;
	const isCompactLandscape = isDesktopLandscape && safeAreaHeight < safeAreaWidth * 0.62;
	const isWideLayout = isDesktopLandscape && safeAreaWidth >= 980;
	const modalAspect = isDesktopLandscape ? 2.25 : 0.72;
	const maxWidthFromHeight = safeAreaHeight * (isDesktopLandscape ? 0.86 : 0.78) * modalAspect;
	const maxWidthFromScreen =
		safeAreaWidth * (isWideLayout ? 0.36 : isDesktopLandscape ? 0.72 : 0.88);
	const modalWidth = Math.min(
		maxWidthFromScreen,
		maxWidthFromHeight,
		isWideLayout ? 500 : isDesktopLandscape ? 540 : 340,
	);
	const modalMaxHeight = safeAreaHeight * (isDesktopLandscape ? 0.86 : 0.78);
	const designHeight = isDesktopLandscape ? modalWidth / modalAspect : modalWidth * 1.42;
	const estimatedContentHeight = modalWidth * (isDesktopLandscape ? 0.63 : 1.42);
	const availableContentHeight = modalMaxHeight - modalWidth * (isDesktopLandscape ? 0.075 : 0.12);
	const compactScale = Math.min(
		1,
		Math.max(
			0.5,
			Math.min(modalMaxHeight / designHeight, availableContentHeight / estimatedContentHeight),
		),
	);
	const contentScale = isCompactLandscape ? compactScale * 0.9 : compactScale;
	const buttonScale = isCompactLandscape ? compactScale * 0.94 : compactScale;
	const scaled = (value: number) => value * compactScale;
	const contentScaled = (value: number) => value * contentScale;
	const buttonScaled = (value: number) => value * buttonScale;

	const handleUnlock = async () => {
		if (isSubmitting) return;

		try {
			setIsSubmitting(true);
			await unlockRoomPurchase({ roomId });
			dispatch(profileRequest());
			onUnlocked?.();
			toast.success(
				'T_SITE_UNLOCK_ROOM_SUCCESS',
				'Room unlocked',
				t('T_SITE_UNLOCK_ROOM_SUCCESS_BODY', {
					defaultValue: '{{room}} is now available.',
					room: roomName,
				}),
			);
		} catch (e: any) {
			const raw = e?.response?.data?.message ?? e?.response?.data ?? e?.message ?? '';
			const message = typeof raw === 'string' ? raw : JSON.stringify(raw);
			const isNotEnoughChips =
				message.toLowerCase().includes('insufficient chips') ||
				message.toLowerCase().includes('not enough chips') ||
				e?.response?.status === 402;
			if (isNotEnoughChips) {
				setShowNotEnoughChips(true);
			} else {
				toast.error(
					'T_SITE_UNLOCK_ROOM_FAILED',
					'Unlock failed',
					message || t('T_SITE_UNLOCK_ROOM_FAILED_BODY', { defaultValue: 'Failed to unlock room' }),
				);
			}
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleBuyChips = () => {
		setShowNotEnoughChips(false);
		onClose();
		navigation.navigate('BuyPackages', { activeTab: 'chips' });
	};

	return (
		<>
			<SvaraModal
				visible
				onClose={onClose}
				containerStyles={{
					width: modalWidth,
					maxHeight: modalMaxHeight,
				}}
				contentWrapperStyles={{
					paddingLeft: scaled(14),
					paddingRight: scaled(12),
					paddingTop: scaled(36),
					paddingBottom: scaled(isCompactLandscape ? 10 : 14),
				}}
			>
				<View
					style={[
						styles.container,
						{ width: '100%', maxHeight: modalMaxHeight },
						isDesktopLandscape && styles.containerLandscape,
					]}
				>
					<View
						style={[
							styles.content,
							{
								paddingHorizontal: contentScaled(isDesktopLandscape ? 16 : 20),
								paddingVertical: contentScaled(isDesktopLandscape ? 10 : 20),
								marginTop: 0,
							},
						]}
					>
						{/* Lock Icon */}
						<View
							style={[
								styles.lockContainer,
								{
									width: contentScaled(isDesktopLandscape ? 78 : 100),
									height: contentScaled(isDesktopLandscape ? 78 : 100),
									marginBottom: contentScaled(isCompactLandscape ? 4 : isDesktopLandscape ? 8 : 15),
								},
							]}
						>
							<Image source={lockImages.lock4_1} style={styles.lockBaseIcon} resizeMode='contain' />
							<Image source={lockImages.lock4_2} style={styles.lockTextIcon} resizeMode='contain' />
						</View>

						{/* Title */}
						<Text
							style={[
								styles.title,
								{
									fontSize: contentScaled(isDesktopLandscape ? 20 : 24),
									marginBottom: contentScaled(
										isCompactLandscape ? 8 : isDesktopLandscape ? 12 : 20,
									),
								},
							]}
						>
							{t('T_SITE_UNLOCK_ROOM_TITLE', { defaultValue: 'Room Locked' })}
						</Text>

						{/* Main Message */}
						<View
							style={[
								styles.messageBox,
								{
									paddingVertical: contentScaled(isDesktopLandscape ? 10 : 15),
									paddingHorizontal: contentScaled(isDesktopLandscape ? 14 : 20),
									marginBottom: contentScaled(
										isCompactLandscape ? 8 : isDesktopLandscape ? 12 : 20,
									),
								},
							]}
						>
							<Text
								style={[
									styles.messageText,
									{
										fontSize: contentScaled(isDesktopLandscape ? 14 : 16),
										lineHeight: contentScaled(isDesktopLandscape ? 20 : 24),
									},
								]}
							>
								{t('T_SITE_UNLOCK_ROOM_NEED_PREFIX', { defaultValue: 'You need at least' })}{' '}
								<Text style={[styles.highlight, { fontSize: contentScaled(18) }]}>
									{t('T_SITE_UNLOCK_ROOM_LEVEL', {
										defaultValue: 'level {{level}}',
										level: minLevel,
									})}
								</Text>{' '}
								{t('T_SITE_UNLOCK_ROOM_NEED_SUFFIX', { defaultValue: 'to play in this room!' })}
							</Text>
						</View>

						{/* Description Box */}
						<View
							style={[
								styles.descriptionBox,
								{
									paddingVertical: contentScaled(isDesktopLandscape ? 10 : 15),
									paddingHorizontal: contentScaled(isDesktopLandscape ? 14 : 20),
									marginBottom: contentScaled(
										isCompactLandscape ? 8 : isDesktopLandscape ? 14 : 30,
									),
								},
							]}
						>
							<Text
								style={[
									styles.descriptionText,
									{
										fontSize: contentScaled(isDesktopLandscape ? 13 : 15),
										lineHeight: contentScaled(isDesktopLandscape ? 18 : 22),
										marginBottom: contentScaled(isDesktopLandscape ? 6 : 10),
									},
								]}
							>
								{t('T_SITE_UNLOCK_ROOM_CAN_UNLOCK_PREFIX', { defaultValue: 'You can unlock' })}{' '}
								<Text style={styles.roomNameHighlight}>{roomName}</Text>{' '}
								{t('T_SITE_UNLOCK_ROOM_CAN_UNLOCK_SUFFIX', { defaultValue: 'room for' })}
							</Text>
							<View
								style={[
									styles.priceContainer,
									{ marginTop: contentScaled(isDesktopLandscape ? 2 : 5) },
								]}
							>
								<Text
									style={[
										styles.priceText,
										{
											fontSize: contentScaled(isDesktopLandscape ? 20 : 24),
											marginRight: contentScaled(8),
										},
									]}
								>
									{roomUnlockCost}
								</Text>
								<Image
									source={ProfileChipPng}
									style={{
										width: contentScaled(isDesktopLandscape ? 22 : 28),
										height: contentScaled(isDesktopLandscape ? 22 : 28),
									}}
									resizeMode='contain'
								/>
							</View>
						</View>

						{/* Buttons */}
						<View
							style={[styles.buttonsContainer, { gap: buttonScaled(isDesktopLandscape ? 10 : 15) }]}
						>
							<TouchableOpacity
								style={[
									styles.button,
									styles.closeButton,
									{ paddingVertical: buttonScaled(isCompactLandscape ? 10 : 12) },
								]}
								onPress={onClose}
								disabled={isSubmitting}
								activeOpacity={0.8}
							>
								<Text
									style={[
										styles.buttonText,
										{ fontSize: buttonScaled(isDesktopLandscape ? 14 : 16) },
									]}
								>
									{t('T_SITE_ACHIEVEMENTS_CLOSE', { defaultValue: 'Close' })}
								</Text>
							</TouchableOpacity>

							<TouchableOpacity
								style={[
									styles.button,
									styles.unlockButton,
									{ paddingVertical: buttonScaled(isCompactLandscape ? 10 : 12) },
								]}
								onPress={handleUnlock}
								disabled={isSubmitting}
								activeOpacity={0.8}
							>
								{isSubmitting ? (
									<ActivityIndicator color='#fff' />
								) : (
									<Text
										style={[
											styles.buttonText,
											{ fontSize: buttonScaled(isDesktopLandscape ? 14 : 16) },
										]}
									>
										{t('T_SITE_UNLOCK', { defaultValue: 'Unlock' })}
									</Text>
								)}
							</TouchableOpacity>
						</View>
					</View>
				</View>
			</SvaraModal>

			<NotEnoughChipsModal
				visible={showNotEnoughChips}
				onClose={() => setShowNotEnoughChips(false)}
				onBuy={handleBuyChips}
			/>
		</>
	);
};

export default UnlockRoomModal;

const FONT = 'Sofia Sans';

const styles = StyleSheet.create({
	container: {
		maxHeight: '85%',
	},
	containerLandscape: {
		maxHeight: '92%',
	},
	content: {
		alignItems: 'center',
	},
	lockContainer: {
		position: 'relative',
		justifyContent: 'center',
		alignItems: 'center',
	},
	lockBaseIcon: {
		position: 'absolute',
		width: '80%',
		height: '80%',
	},
	lockTextIcon: {
		position: 'absolute',
		width: '80%',
		height: '80%',
	},
	title: {
		fontFamily: FONT,
		color: redesignPanelColors.title,
		fontWeight: '800',
		textAlign: 'center',
		textTransform: 'uppercase',
		textShadowColor: 'rgba(255, 114, 0, 0.6)',
		textShadowOffset: { width: 0, height: 0 },
		textShadowRadius: 6,
	},
	messageBox: {
		width: '100%',
		backgroundColor: redesignPanelColors.tileFill,
		borderRadius: 12,
		borderWidth: 1,
		borderColor: redesignPanelColors.tileBorder,
	},
	messageText: {
		fontFamily: FONT,
		fontWeight: '500',
		color: redesignPanelColors.white,
		textAlign: 'center',
	},
	highlight: {
		color: redesignPanelColors.gold,
		fontWeight: '800',
	},
	descriptionBox: {
		width: '100%',
		backgroundColor: redesignPanelColors.surface,
		borderRadius: 12,
		alignItems: 'center',
	},
	descriptionText: {
		fontFamily: FONT,
		fontWeight: '500',
		color: redesignPanelColors.white,
		textAlign: 'center',
	},
	roomNameHighlight: {
		color: redesignPanelColors.gold,
		fontWeight: '800',
		textTransform: 'capitalize',
	},
	priceContainer: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'center',
	},
	priceText: {
		fontFamily: FONT,
		color: redesignPanelColors.title,
		fontWeight: '800',
	},
	buttonsContainer: {
		flexDirection: 'row',
		width: '100%',
	},
	button: {
		flex: 1,
		borderRadius: 10,
		borderWidth: 1,
		alignItems: 'center',
		justifyContent: 'center',
	},
	closeButton: {
		backgroundColor: redesignPanelColors.tileFill,
		borderColor: redesignPanelColors.tileBorder,
	},
	// The green of the redesign's CLAIM / Great! buttons.
	unlockButton: {
		backgroundColor: '#248F1C',
		borderColor: 'rgba(184,255,140,0.75)',
	},
	buttonText: {
		fontFamily: FONT,
		color: redesignPanelColors.white,
		fontWeight: '800',
		textTransform: 'uppercase',
	},
});
